import * as React from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  isAfter,
  getHours,
  getMinutes,
  startOfDay,
  endOfDay,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, List, MoreHorizontal, Pencil, Trash2, Play } from 'lucide-react';
import { Button } from '@systemzero-inc/ui/primitives/button';
import { Calendar } from '~/components/ui/calendar';
import { ToggleGroup, ToggleGroupItem } from '~/components/ui/toggle-group';
import {
  useScheduledTasks,
  useCreateScheduledTask,
  useUpdateScheduledTask,
  useCancelScheduledTask,
  useDeleteScheduledTask,
  useExecuteScheduledTask,
} from '~/features/scheduled-tasks/hooks';
import { TaskEventDialog } from '~/features/scheduled-tasks/components/task-event-dialog/task-event-dialog';
import type { ScheduledTask } from '~/data/endpoints/scheduled-tasks';
import type { CreateScheduledTaskBody } from '~/data/endpoints/scheduled-tasks';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { cn } from '~/lib/utils';
import { toast } from '~/hooks/use-toast';
import { isValid } from 'date-fns';

type ViewMode = 'day' | 'week' | 'month';

function statusColor(status: string | undefined): string {
  switch (status) {
    case 'COMPLETED':
      return 'bg-positive-background-hard text-positive-foreground shadow-sm hover:brightness-95';
    case 'FAILED':
      return 'bg-negative-background-hard text-negative-foreground shadow-sm hover:brightness-95';
    case 'CANCELLED':
      return 'bg-muted text-muted-foreground shadow-sm hover:bg-muted/80';
    case 'RUNNING':
      return 'bg-alert-background-hard text-alert-foreground shadow-sm animate-pulse';
    default:
      return 'bg-completed-background-hard text-completed-foreground shadow-sm hover:brightness-95';
  }
}

function safeDate(value: unknown): Date {
  if (value instanceof Date && isValid(value)) return value;
  const d = new Date(value as string | number);
  return isValid(d) ? d : new Date();
}

function safeFormat(value: unknown, fmt: string): string {
  try {
    return format(safeDate(value), fmt);
  } catch {
    return '';
  }
}

function expandRecurringTasks(tasks: ScheduledTask[], rangeStart: Date, rangeEnd: Date): ScheduledTask[] {
  const result: ScheduledTask[] = [];
  for (const task of tasks) {
    result.push(task);
    if (!task.recurrence || task.recurrence === 'once' || (task.status !== 'PENDING' && task.status !== 'SCHEDULED')) {
      continue;
    }
    const base = safeDate(task.scheduledAt);
    const hours = base.getHours();
    const minutes = base.getMinutes();
    let cursor = nextOccurrence(base, task.recurrence);
    let guard = 0;
    while (cursor <= rangeEnd && guard < 200) {
      if (cursor >= rangeStart) {
        result.push({
          ...task,
          id: `${task.id}__proj_${format(cursor, 'yyyy-MM-dd')}`,
          scheduledAt: cursor.toISOString(),
          status: 'SCHEDULED',
        } as ScheduledTask);
      }
      cursor = nextOccurrence(cursor, task.recurrence);
      guard++;
    }
    function nextOccurrence(from: Date, recurrence: string): Date {
      const d = new Date(from);
      switch (recurrence) {
        case 'daily':
          d.setDate(d.getDate() + 1);
          break;
        case 'weekdays': {
          d.setDate(d.getDate() + 1);
          while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
          break;
        }
        case 'weekly':
          d.setDate(d.getDate() + 7);
          break;
        case 'biweekly':
          d.setDate(d.getDate() + 14);
          break;
        case 'monthly':
          d.setMonth(d.getMonth() + 1);
          break;
        default:
          d.setFullYear(d.getFullYear() + 100);
      }
      d.setHours(hours, minutes, 0, 0);
      return d;
    }
  }
  return result;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const HOUR_HEIGHT = 56;
const HALF_HOUR = HOUR_HEIGHT / 2;
const GRID_HEIGHT = HOURS.length * HOUR_HEIGHT;
const DEFAULT_DURATION_MIN = 30;
const GUTTER_WIDTH = 52;
const HEADER_HEIGHT = 48;
const ALL_DAY_HEIGHT = 32;

function getViewRange(date: Date, mode: ViewMode): { from: Date; to: Date } {
  switch (mode) {
    case 'day':
      return { from: startOfDay(date), to: endOfDay(date) };
    case 'week': {
      const ws = startOfWeek(date, { weekStartsOn: 1 });
      const we = endOfWeek(date, { weekStartsOn: 1 });
      return { from: startOfDay(ws), to: endOfDay(we) };
    }
    case 'month':
    default:
      return { from: startOfDay(startOfMonth(date)), to: endOfDay(endOfMonth(date)) };
  }
}

function navigateView(date: Date, mode: ViewMode, direction: 'prev' | 'next'): Date {
  switch (mode) {
    case 'day':
      return direction === 'next' ? addDays(date, 1) : subDays(date, 1);
    case 'week':
      return direction === 'next' ? addWeeks(date, 1) : subWeeks(date, 1);
    case 'month':
    default:
      return direction === 'next' ? addMonths(date, 1) : subMonths(date, 1);
  }
}

function viewLabel(date: Date, mode: ViewMode): string {
  switch (mode) {
    case 'day':
      return format(date, 'EEEE, d MMMM yyyy');
    case 'week': {
      const ws = startOfWeek(date, { weekStartsOn: 1 });
      const we = endOfWeek(date, { weekStartsOn: 1 });
      return `${format(ws, 'd MMMM')} – ${format(we, 'd MMMM yyyy')}`;
    }
    case 'month':
    default:
      return format(date, 'MMMM yyyy');
  }
}

function timeToY(d: Date): number {
  return (getHours(d) + getMinutes(d) / 60) * HOUR_HEIGHT;
}

/* ─── Time grid: Day & Week ──────────────────────────────────────── */

function TimeGutter({ showTzLabel }: { showTzLabel?: boolean }) {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
  const short = tz.includes('/') ? (tz.split('/').pop() ?? tz).replace(/_/g, ' ') : tz;

  return (
    <div className="relative shrink-0 border-r border-border" style={{ width: GUTTER_WIDTH }}>
      {showTzLabel && (
        <div
          className="flex items-center justify-center text-[9px] font-medium text-muted-foreground uppercase tracking-wider border-b border-border"
          style={{ height: HEADER_HEIGHT }}
        >
          {short}
        </div>
      )}
      <div
        className="flex items-center justify-center text-[9px] text-muted-foreground border-b border-border"
        style={{ height: ALL_DAY_HEIGHT }}
      >
        All Day
      </div>
      <div className="relative" style={{ height: GRID_HEIGHT }}>
        {HOURS.map((h) => (
          <div
            key={h}
            className="absolute left-0 right-0 text-[10px] text-muted-foreground text-right pr-2 pointer-events-none"
            style={{ top: h * HOUR_HEIGHT - 7 }}
          >
            {h === 0 ? '' : format(new Date(2000, 0, 1, h), 'HH:mm')}
          </div>
        ))}
      </div>
    </div>
  );
}

function NowIndicator() {
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const now = new Date();
  const top = timeToY(now);
  return (
    <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top }}>
      <div className="flex items-center">
        <div className="size-2.5 rounded-full bg-primary -ml-[5px] shrink-0 ring-2 ring-background" />
        <div className="h-[2px] flex-1 bg-primary" />
      </div>
    </div>
  );
}

function EventBlock({
  task,
  onEdit,
  onDelete,
  onExecute,
  canEdit,
}: {
  task: ScheduledTask;
  onEdit: (task: ScheduledTask) => void;
  onDelete: (task: ScheduledTask) => void;
  onExecute: (task: ScheduledTask) => void;
  canEdit: boolean;
}) {
  const d = safeDate(task.scheduledAt);
  const top = timeToY(d);
  const height = Math.max((DEFAULT_DURATION_MIN / 60) * HOUR_HEIGHT, 24);
  const projected = task.id.includes('__proj_');

  return (
    <div
      data-event-block
      role={canEdit ? 'button' : undefined}
      tabIndex={canEdit ? 0 : undefined}
      className={cn(
        'absolute left-1 right-1 z-10 rounded-[4px] px-2 py-1 text-[11px] text-left overflow-hidden transition-all group',
        statusColor(task.status),
        'hover:shadow-md',
        projected && 'opacity-50 border border-dashed border-current',
        canEdit ? 'cursor-pointer' : 'cursor-default',
      )}
      style={{ top, height }}
      onClick={() => canEdit && onEdit(task)}
      onKeyDown={(e) => {
        if (canEdit && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onEdit(task);
        }
      }}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0 flex-1">
          <div className="font-semibold leading-tight truncate">{task.title}</div>
          {height >= 40 && (
            <div className="text-[10px] opacity-70 leading-tight mt-0.5 truncate">
              {format(d, 'HH:mm')} · {task.description || task.prompt?.slice(0, 60)}
            </div>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              title="Task actions"
              className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 rounded p-0.5 hover:bg-black/10 dark:hover:bg-white/20"
            >
              <MoreHorizontal className="size-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            {canEdit && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onExecute(task);
                }}
              >
                <Play className="size-3.5 mr-2" /> Run now
              </DropdownMenuItem>
            )}
            {canEdit && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(task);
                }}
              >
                <Pencil className="size-3.5 mr-2" /> Edit
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(task);
              }}
            >
              <Trash2 className="size-3.5 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function DayColumn({
  day,
  tasks,
  onEdit,
  onDelete,
  onExecute,
  canEdit,
  showHeader,
  isSingle,
  onSlotClick,
}: {
  day: Date;
  tasks: ScheduledTask[];
  onEdit: (task: ScheduledTask) => void;
  onDelete: (task: ScheduledTask) => void;
  onExecute: (task: ScheduledTask) => void;
  canEdit: (task: ScheduledTask) => boolean;
  showHeader: boolean;
  isSingle: boolean;
  onSlotClick?: (date: Date) => void;
}) {
  const isToday = isSameDay(day, new Date());
  const gridRef = React.useRef<HTMLDivElement>(null);

  const handleGridClick = (e: React.MouseEvent) => {
    if (!onSlotClick || !gridRef.current) return;
    if ((e.target as HTMLElement).closest('[data-event-block]') || (e.target as HTMLElement).closest('button')) return;
    const rect = gridRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const totalMinutes = Math.floor((y / HOUR_HEIGHT) * 60);
    const snappedMinutes = Math.floor(totalMinutes / 30) * 30;
    const hours = Math.floor(snappedMinutes / 60);
    const mins = snappedMinutes % 60;
    const clicked = new Date(day);
    clicked.setHours(hours, mins, 0, 0);
    onSlotClick(clicked);
  };

  return (
    <div className={cn('relative flex-1 min-w-0 flex flex-col', isSingle && 'flex-1')}>
      {showHeader && (
        <div
          className={cn(
            'flex flex-col items-center justify-center border-b border-border shrink-0',
            isToday ? 'bg-primary/5' : 'bg-background',
          )}
          style={{ height: HEADER_HEIGHT }}
        >
          <p
            className={cn(
              'text-[10px] uppercase tracking-wider',
              isToday ? 'text-primary font-semibold' : 'text-muted-foreground',
            )}
          >
            {format(day, 'EEE')}
          </p>
          <div
            className={cn(
              'text-lg font-semibold leading-none mt-0.5',
              isToday
                ? 'bg-primary text-primary-foreground rounded-full size-7 flex items-center justify-center'
                : 'text-foreground',
            )}
          >
            {format(day, 'd')}
          </div>
        </div>
      )}
      {/* All Day row */}
      <div
        className={cn('border-b border-border shrink-0', isToday && 'bg-primary/2')}
        style={{ height: ALL_DAY_HEIGHT }}
      />
      {/* Time grid */}
      <div
        ref={gridRef}
        role="grid"
        tabIndex={0}
        className={cn('relative flex-1 cursor-cell', isToday && 'bg-primary/2')}
        style={{ height: GRID_HEIGHT }}
        onClick={handleGridClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onSlotClick) {
            const d = new Date(day);
            d.setHours(9, 0, 0, 0);
            onSlotClick(d);
          }
        }}
      >
        {HOURS.map((h) => (
          <React.Fragment key={h}>
            <div className="absolute left-0 right-0 border-b border-border" style={{ top: h * HOUR_HEIGHT }} />
            <div
              className="absolute left-0 right-0 border-b border-border/40 border-dashed"
              style={{ top: h * HOUR_HEIGHT + HALF_HOUR }}
            />
          </React.Fragment>
        ))}
        {isToday && <NowIndicator />}
        {tasks.map((task) => (
          <EventBlock
            key={task.id}
            task={task}
            onEdit={onEdit}
            onDelete={onDelete}
            onExecute={onExecute}
            canEdit={canEdit(task)}
          />
        ))}
      </div>
    </div>
  );
}

function TimeGridView({
  days,
  tasksByDay,
  onEdit,
  onDelete,
  onExecute,
  canEdit,
  onSlotClick,
}: {
  days: Date[];
  tasksByDay: Map<string, ScheduledTask[]>;
  onEdit: (task: ScheduledTask) => void;
  onDelete: (task: ScheduledTask) => void;
  onExecute: (task: ScheduledTask) => void;
  canEdit: (task: ScheduledTask) => boolean;
  onSlotClick?: (date: Date) => void;
}) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const didScroll = React.useRef(false);

  React.useEffect(() => {
    if (didScroll.current) return;
    const el = scrollRef.current;
    if (el) {
      const now = new Date();
      const target = Math.max(0, (getHours(now) - 1) * HOUR_HEIGHT);
      el.scrollTop = target;
      didScroll.current = true;
    }
  });

  const isSingle = days.length === 1;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
  const tzShort = tz.includes('/') ? (tz.split('/').pop() ?? tz).replace(/_/g, ' ') : tz;

  return (
    <div className="h-full flex flex-col rounded-lg border border-border bg-background overflow-hidden">
      {/* Sticky header row */}
      {!isSingle && (
        <div className="flex shrink-0 border-b border-border bg-background z-20">
          <div
            className="shrink-0 border-r border-border flex items-center justify-center text-[9px] font-medium text-muted-foreground uppercase tracking-wider"
            style={{ width: GUTTER_WIDTH, height: HEADER_HEIGHT }}
          >
            {tzShort}
          </div>
          <div className="flex flex-1 divide-x divide-border min-w-0">
            {days.map((day) => {
              const isToday = isSameDay(day, new Date());
              return (
                <div
                  key={format(day, 'yyyy-MM-dd')}
                  className={cn(
                    'flex-1 min-w-0 flex flex-col items-center justify-center shrink-0',
                    isToday ? 'bg-primary/5' : 'bg-background',
                  )}
                  style={{ height: HEADER_HEIGHT }}
                >
                  <p
                    className={cn(
                      'text-[10px] uppercase tracking-wider',
                      isToday ? 'text-primary font-semibold' : 'text-muted-foreground',
                    )}
                  >
                    {format(day, 'EEE')}
                  </p>
                  <div
                    className={cn(
                      'text-lg font-semibold leading-none mt-0.5',
                      isToday
                        ? 'bg-primary text-primary-foreground rounded-full size-7 flex items-center justify-center'
                        : 'text-foreground',
                    )}
                  >
                    {format(day, 'd')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Scrollable grid area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
        <div className="flex min-w-0">
          <div className="sticky left-0 z-30 bg-background">
            <TimeGutter showTzLabel={false} />
          </div>
          <div className="flex flex-1 divide-x divide-border min-w-0">
            {days.map((day) => {
              const key = format(day, 'yyyy-MM-dd');
              return (
                <DayColumn
                  key={key}
                  day={day}
                  tasks={tasksByDay.get(key) ?? []}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onExecute={onExecute}
                  canEdit={canEdit}
                  showHeader={false}
                  isSingle={isSingle}
                  onSlotClick={onSlotClick}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Month view ─────────────────────────────────────────────────── */

function MonthView({
  days,
  tasksByDay,
  onEdit,
  canEdit,
  month,
  onSlotClick,
}: {
  days: Date[];
  tasksByDay: Map<string, ScheduledTask[]>;
  onEdit: (task: ScheduledTask) => void;
  canEdit: (task: ScheduledTask) => boolean;
  month: Date;
  onSlotClick?: (date: Date) => void;
}) {
  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return (
    <div className="rounded-lg overflow-hidden border border-border flex flex-col h-full bg-background">
      {/* Day-of-week headers — same border style as the time grid */}
      <div className="grid grid-cols-7 shrink-0 border-b border-border">
        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((d) => (
          <div
            key={d}
            className="px-2 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground text-center border-r last:border-r-0 border-border/50"
          >
            {d}
          </div>
        ))}
      </div>
      {/* Week rows */}
      <div className="flex-1 flex flex-col min-h-0">
        {weeks.map((week, wi) => (
          <div key={wi} className="flex-1 grid grid-cols-7 min-h-0 border-b last:border-b-0 border-border/50">
            {week.map((day) => {
              const key = format(day, 'yyyy-MM-dd');
              const dayTasks = tasksByDay.get(key) ?? [];
              const isToday = isSameDay(day, new Date());
              const inMonth = isSameMonth(day, month);
              return (
                <div
                  key={key}
                  role="gridcell"
                  tabIndex={0}
                  className={cn(
                    'flex flex-col min-h-0 overflow-hidden cursor-cell border-r last:border-r-0 border-border/50',
                    !inMonth && 'bg-muted/20',
                    isToday && 'bg-primary/2',
                  )}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('button')) return;
                    if (onSlotClick) {
                      const d = new Date(day);
                      d.setHours(9, 0, 0, 0);
                      onSlotClick(d);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && onSlotClick) {
                      const d = new Date(day);
                      d.setHours(9, 0, 0, 0);
                      onSlotClick(d);
                    }
                  }}
                >
                  <div className="px-1.5 pt-1 shrink-0">
                    <span
                      className={cn(
                        'text-xs inline-flex items-center justify-center',
                        isToday
                          ? 'bg-primary text-primary-foreground rounded-full size-6 font-bold'
                          : inMonth
                            ? 'text-foreground font-medium'
                            : 'text-muted-foreground',
                      )}
                    >
                      {format(day, 'd')}
                    </span>
                  </div>
                  <div className="flex-1 overflow-y-auto px-0.5 pb-0.5 space-y-[2px] mt-0.5">
                    {dayTasks.slice(0, 4).map((task) => (
                      <button
                        key={task.id}
                        type="button"
                        className={cn(
                          'w-full text-left rounded-[4px] px-1.5 py-[3px] text-[10px] leading-tight truncate transition-all hover:shadow-md',
                          statusColor(task.status),
                          canEdit(task) ? 'cursor-pointer' : 'cursor-default',
                        )}
                        onClick={() => canEdit(task) && onEdit(task)}
                      >
                        {safeFormat(task.scheduledAt, 'HH:mm')} {task.title}
                      </button>
                    ))}
                    {dayTasks.length > 4 && (
                      <p className="text-[10px] text-primary font-medium px-1.5 cursor-pointer hover:underline">
                        +{dayTasks.length - 4} more
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Upcoming sidebar ───────────────────────────────────────────── */

function UpcomingTasks({
  tasks,
  onEdit,
  canEdit,
}: {
  tasks: ScheduledTask[];
  onEdit: (task: ScheduledTask) => void;
  canEdit: (task: ScheduledTask) => boolean;
}) {
  const now = new Date();
  const upcoming = tasks
    .filter((t) => (t.status === 'PENDING' || t.status === 'SCHEDULED') && isAfter(safeDate(t.scheduledAt), now))
    .sort((a, b) => safeDate(a.scheduledAt).getTime() - safeDate(b.scheduledAt).getTime())
    .slice(0, 5);

  if (upcoming.length === 0) {
    return <p className="text-xs text-muted-foreground text-center py-3">No upcoming tasks</p>;
  }

  return (
    <ul className="space-y-1.5">
      {upcoming.map((task) => (
        <li key={task.id}>
          <button
            type="button"
            className="w-full text-left rounded-md border px-3 py-2 hover:bg-muted/50 transition-colors cursor-pointer"
            onClick={() => canEdit(task) && onEdit(task)}
          >
            <p className="text-sm font-medium truncate">{task.title}</p>
            <p className="text-xs text-muted-foreground">{safeFormat(task.scheduledAt, 'EEE, MMM d · HH:mm')}</p>
          </button>
        </li>
      ))}
    </ul>
  );
}

/* ─── Main ───────────────────────────────────────────────────────── */

export function TaskCalendar() {
  const [viewDate, setViewDate] = React.useState(() => new Date());
  const [viewMode, setViewMode] = React.useState<ViewMode>('week');
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(undefined);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingTask, setEditingTask] = React.useState<ScheduledTask | null>(null);

  const calendarMonth = React.useMemo(() => {
    return viewMode === 'month' ? viewDate : startOfMonth(viewDate);
  }, [viewDate, viewMode]);

  const { from, to } = getViewRange(viewDate, viewMode);
  const calFrom = startOfDay(startOfMonth(calendarMonth));
  const calTo = endOfDay(endOfMonth(calendarMonth));

  const fetchFrom = calFrom < from ? calFrom : from;
  const fetchTo = calTo > to ? calTo : to;
  const { data: tasks = [], isLoading } = useScheduledTasks({
    from: fetchFrom.toISOString(),
    to: fetchTo.toISOString(),
  });

  const createMutation = useCreateScheduledTask();
  const updateMutation = useUpdateScheduledTask();
  const cancelMutation = useCancelScheduledTask();
  const deleteMutation = useDeleteScheduledTask();
  const executeMutation = useExecuteScheduledTask();

  const expandedTasks = React.useMemo(
    () => expandRecurringTasks(tasks, fetchFrom, fetchTo),
    [tasks, fetchFrom, fetchTo],
  );

  const tasksByDay = React.useMemo(() => {
    const map = new Map<string, ScheduledTask[]>();
    for (const task of expandedTasks) {
      const day = safeFormat(task.scheduledAt, 'yyyy-MM-dd');
      if (!day) continue;
      const existing = map.get(day);
      if (existing) {
        existing.push(task);
      } else {
        map.set(day, [task]);
      }
    }
    return map;
  }, [expandedTasks]);

  const viewDays = React.useMemo(() => {
    if (viewMode === 'day') return [viewDate];
    if (viewMode === 'week') {
      const ws = startOfWeek(viewDate, { weekStartsOn: 1 });
      const we = endOfWeek(viewDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start: ws, end: we });
    }
    const ms = startOfWeek(startOfMonth(viewDate), { weekStartsOn: 1 });
    const me = endOfWeek(endOfMonth(viewDate), { weekStartsOn: 1 });
    return eachDayOfInterval({ start: ms, end: me });
  }, [viewDate, viewMode]);

  const modifiers = React.useMemo(
    () => ({
      hasTask: (d: Date) => tasksByDay.has(format(d, 'yyyy-MM-dd')),
    }),
    [tasksByDay],
  );

  const modifiersClassNames = {
    hasTask: 'bg-amber-600/10 font-bold',
  };

  const handleSubmit = async (body: CreateScheduledTaskBody, taskId?: string) => {
    try {
      if (taskId) {
        await updateMutation.mutateAsync({ id: taskId, body });
        toast({ title: 'Task updated' });
      } else {
        await createMutation.mutateAsync(body);
        toast({ title: 'Task scheduled' });
      }
      setEditingTask(null);
      setDialogOpen(false);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const msg = axiosErr?.response?.data?.message ?? (err instanceof Error ? err.message : 'Something went wrong');
      toast({ title: 'Failed to save task', description: msg, variant: 'destructive' });
    }
  };

  const handleCancelTask = async (id: string) => {
    try {
      await cancelMutation.mutateAsync(id);
      toast({ title: 'Task cancelled' });
      setEditingTask(null);
      setDialogOpen(false);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const msg = axiosErr?.response?.data?.message ?? (err instanceof Error ? err.message : 'Something went wrong');
      toast({ title: 'Failed to cancel task', description: msg, variant: 'destructive' });
    }
  };

  const isProjected = (task: ScheduledTask) => task.id.includes('__proj_');
  const canEdit = (task: ScheduledTask) =>
    !isProjected(task) && (task.status === 'PENDING' || task.status === 'SCHEDULED');

  const handleDeleteTask = async (task: ScheduledTask) => {
    try {
      await deleteMutation.mutateAsync(task.id);
      toast({ title: 'Task deleted' });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const msg = axiosErr?.response?.data?.message ?? (err instanceof Error ? err.message : 'Something went wrong');
      toast({ title: 'Failed to delete task', description: msg, variant: 'destructive' });
    }
  };

  const handleExecuteTask = async (task: ScheduledTask) => {
    try {
      await executeMutation.mutateAsync(task.id);
      toast({ title: 'Task execution started', description: `"${task.title}" is now running` });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const msg = axiosErr?.response?.data?.message ?? (err instanceof Error ? err.message : 'Something went wrong');
      toast({ title: 'Failed to execute task', description: msg, variant: 'destructive' });
    }
  };

  const handleCalendarSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date) {
      setViewDate(date);
      setViewMode('day');
    }
  };

  const handleSlotClick = (date: Date) => {
    setSelectedDate(date);
    setEditingTask(null);
    setDialogOpen(true);
  };

  return (
    <div className="mt-2 flex flex-col h-[calc(100vh-130px)]">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 shrink-0 pb-3">
        <div className="flex items-center gap-3">
          <Button
            onClick={() => {
              setEditingTask(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="size-4" />
            New task
          </Button>
          <div className="h-6 w-px bg-border" />
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => {
              const now = new Date();
              setViewDate(now);
              setSelectedDate(now);
            }}
          >
            Today
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={() => setViewDate((d) => navigateView(d, viewMode, 'prev'))}
            aria-label="Previous"
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={() => setViewDate((d) => navigateView(d, viewMode, 'next'))}
            aria-label="Next"
          >
            <ChevronRight className="size-4" />
          </Button>
          <span className="font-semibold text-base">{viewLabel(viewDate, viewMode)}</span>
        </div>
        <ToggleGroup type="single" value={viewMode} onValueChange={(v) => v && setViewMode(v as ViewMode)} size="sm">
          <ToggleGroupItem value="day" aria-label="Day view" className="text-xs px-3">
            Day
          </ToggleGroupItem>
          <ToggleGroupItem value="week" aria-label="Week view" className="text-xs px-3">
            Week
          </ToggleGroupItem>
          <ToggleGroupItem value="month" aria-label="Month view" className="text-xs px-3">
            Month
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* Main content */}
      <div className="grid gap-5 lg:grid-cols-[240px_1fr] flex-1 min-h-0">
        {/* Left sidebar: mini calendar + upcoming */}
        <div className="space-y-4 overflow-y-auto">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleCalendarSelect}
            month={calendarMonth}
            onMonthChange={(m) => setViewDate(m)}
            modifiers={modifiers}
            modifiersClassNames={modifiersClassNames}
          />
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5 uppercase tracking-wider">
              <List className="size-3" />
              Upcoming
            </h3>
            {isLoading ? (
              <p className="text-xs text-muted-foreground">Loading…</p>
            ) : (
              <UpcomingTasks
                tasks={expandedTasks}
                onEdit={(task) => {
                  setEditingTask(task);
                  setDialogOpen(true);
                }}
                canEdit={canEdit}
              />
            )}
          </div>
        </div>

        {/* Right: calendar view */}
        <div className="flex-1 min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-muted-foreground">Loading…</p>
            </div>
          ) : viewMode === 'month' ? (
            <MonthView
              days={viewDays}
              tasksByDay={tasksByDay}
              month={viewDate}
              onEdit={(task) => {
                setEditingTask(task);
                setDialogOpen(true);
              }}
              canEdit={canEdit}
              onSlotClick={handleSlotClick}
            />
          ) : (
            <TimeGridView
              days={viewDays}
              tasksByDay={tasksByDay}
              onEdit={(task) => {
                setEditingTask(task);
                setDialogOpen(true);
              }}
              onDelete={handleDeleteTask}
              onExecute={handleExecuteTask}
              canEdit={canEdit}
              onSlotClick={handleSlotClick}
            />
          )}
        </div>
      </div>

      <TaskEventDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initialTask={editingTask}
        defaultDate={selectedDate ?? viewDate}
        onSubmit={handleSubmit}
        onCancelTask={editingTask ? () => handleCancelTask(editingTask.id) : undefined}
        onExecuteTask={editingTask ? () => handleExecuteTask(editingTask) : undefined}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
}
